import type { FeedType } from '@kanbu/schema';
import { FeedStatus } from '@kanbu/schema/enums';
import { useLingui } from '@lingui/react/macro';
import { LoaderCircle } from 'lucide-react';

import { useEnumTranslations } from '@/hooks';

export function getFeedImportCounts(feed: FeedType) {
  const found = feed.importMetadata?.totalProducts;
  const importing =
    feed.status === FeedStatus.Processing || feed.status === FeedStatus.Pending;
  const processed = feed.importMetadata?.productImport?.total ?? 0;

  return {
    found,
    awaiting:
      importing && found !== undefined ? Math.max(0, found - processed) : 0,
  };
}

export function FeedImportProgress({
  feed,
  showCounts = true,
}: {
  feed: FeedType;
  showCounts?: boolean;
}) {
  const { t } = useLingui();
  const translations = useEnumTranslations();
  const metadata = feed.importMetadata;
  const { found, awaiting } = getFeedImportCounts(feed);
  const importing =
    feed.status === FeedStatus.Processing || feed.status === FeedStatus.Pending;
  const embedding = feed.status === FeedStatus.Embedding;
  const rejected = metadata?.productImport?.parserSkipped ?? 0;
  const failed = metadata?.productImport?.failed ?? 0;
  const stage = metadata?.phase;
  const stageLabels = {
    downloading: t`Downloading feed`,
    parsing: t`Parsing feed`,
    importing: t`Importing products`,
    scraping: t`Scraping product pages`,
    enriching: t`Enriching product details`,
    embedding: t`Indexing products for search`,
  };
  let label =
    translations.feedHealth[feed.health] ??
    translations.feedStatus[feed.status];
  if (feed.status === FeedStatus.Done && (rejected > 0 || failed > 0))
    label = t`Completed with issues`;
  if (feed.status === FeedStatus.Pending) label = t`Queued for import`;
  if (feed.status === FeedStatus.Processing)
    label = stage ? stageLabels[stage] : t`Downloading and reading feed`;
  if (embedding) label = stageLabels.embedding;
  if (feed.processingPaused && stage) label = stageLabels[stage];
  const pausing =
    feed.processingPaused &&
    (feed.status === FeedStatus.Processing ||
      Number(feed.embeddingProductsCount) > 0);
  const active = !feed.processingPaused && (importing || embedding);

  return (
    <div className='flex flex-col gap-1 py-1 text-xs' role='status'>
      <span className='flex items-center gap-1.5 font-medium'>
        {(active || pausing) && (
          <LoaderCircle className='size-3 animate-spin' aria-hidden />
        )}
        {feed.processingPaused
          ? pausing
            ? t`Pausing after the current batch`
            : t`Paused`
          : label}
      </span>
      {feed.processingPaused && <span className='text-gray-500'>{label}</span>}
      {pausing && (
        <span className='text-gray-500'>{t`In-flight requests may finish. Completed work is kept.`}</span>
      )}
      {rejected > 0 && (
        <div className='text-amber-700'>
          <span>
            {t`Rejected invalid feed entries`}: {rejected}
          </span>
          <p>{t`These entries were not imported. Correct the source feed and refresh it.`}</p>
          {!!metadata?.parserErrors?.length && (
            <details>
              <summary>{t`Example validation errors`}</summary>
              <ul>
                {metadata.parserErrors.map((error, index) => (
                  <li key={`${error.externalId}-${index}`}>
                    {error.externalId}: {error.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {failed > 0 && (
        <span className='text-amber-700'>
          {t`Failed product imports`}: {failed}
        </span>
      )}
      {showCounts && found !== undefined && (
        <span className='text-gray-500'>
          {t`Found`}: {found}
        </span>
      )}
      {showCounts && awaiting > 0 && (
        <span className='text-gray-500'>
          {t`Awaiting import`}: {awaiting}
        </span>
      )}
    </div>
  );
}
